import { trackMusicTrackDownloaded } from '@src/analytics/EventTracker';
import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DownloadIcon from '@src/svgs/DownloadIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  downloadAudioBuffer,
  downloadAudioBufferAsMp3,
  downloadRemote,
  formatBytes,
} from './audioDownload';

import type { FC, MouseEvent } from 'react';

const logger = LogManager.getLogger('feature:musicDownloads');

/**
 * Kebab ("⋮") actions menu for the "Music and Sounds" control. Holds the
 * secondary actions so the row itself stays a single, constant-width line
 * (PLAT-10626):
 *   - **Reprocess Track Separation** (re-run the split, PLAT-10622)
 *   - a divider, then the **Downloads** (PLAT-10627 / DE6 QA): the isolated
 *     voice stem, the kept background+music bed, the composed AI dub voice
 *     (encoded client-side), and the un-separated original source.
 * Each item only appears when its source is available. Portaled +
 * viewport-clamped, matching `SpeakerRowActionsMenu`.
 */
/** Download analytics track types (PLAT-10712). */
type TrackType = 'voice' | 'background_music' | 'ai_dub' | 'original';

/**
 * A downloadable format under a track (PLAT-10715): a display label (MP3 / WAV
 * / the source's native type), byte size when known, and the download run.
 */
interface FormatOption {
  label: string;
  bytes?: number;
  run: () => void;
}

/** A menu row: the Reprocess action, or a track that opens its format flyout. */
type MenuEntry =
  | { kind: 'action'; label: string; run: () => void; disabled?: boolean }
  | {
      kind: 'group';
      label: string;
      trackType: TrackType;
      formats: FormatOption[];
    };

interface Props {
  /** react-tooltip id for the kebab's "Music actions" hint (PLAT-11563). */
  tooltipId?: string;
  /** Story id, for analytics (PLAT-10712). */
  storyId?: string;
  /** Story title — the download filename stem (PLAT-10715). */
  storyName?: string | null;
  /** Provider of the current stems, for analytics passthrough (PLAT-10712); may be null before it's read. */
  provider: string | null;
  /** Re-run separation; omit to hide the Re-separate item. */
  onReseparate?: () => void;
  /** Disable Re-separate (e.g. while a separation is running or the track is too long). */
  reseparateDisabled?: boolean;
  /**
   * Per-format download URLs for the voice / bed stems (PLAT-10715): MP3
   * (compact) and WAV (lossless). `null` when that format isn't available.
   */
  voiceUrlMp3: string | null;
  voiceUrlWav: string | null;
  bedUrlMp3: string | null;
  bedUrlWav: string | null;
  /** Signed URL of the un-separated source audio (single native format). */
  originalUrl: string | null;
  /** Byte size of each server-served track, for the file-size labels (PLAT-10715). */
  sizes?: {
    bedMp3?: number;
    bedWav?: number;
    voiceMp3?: number;
    voiceWav?: number;
    original?: number;
  };
  /** The composed AI dub track; encoded client-side to MP3 or WAV on download (PLAT-10661/10715). */
  dubBuffer: AudioBuffer | null;
  /** BCP-47 code of the dub language, for analytics. */
  dubLanguage: string | null;
}

const MENU_WIDTH = 250;
const MENU_ITEM_HEIGHT = 40;
const SUBMENU_WIDTH = 170;
/** Bitrate the AI-dub MP3 is encoded at (see `audioBufferToMp3Blob`); used to estimate its size. */
const AI_DUB_KBPS = 128;

/** Story title → filename-safe slug: lowercase, spaces→dashes, punctuation dropped. */
function slugifyStory(name: string | null | undefined): string {
  const slug = (name ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || 'story';
}

/** Lowercase file extension (no dot) parsed from a URL's path, or '' if none. */
function extFromUrl(url: string): string {
  try {
    const seg = new URL(url).pathname.split('/').pop() ?? '';
    const dot = seg.lastIndexOf('.');
    return dot > 0 ? seg.slice(dot + 1).toLowerCase() : '';
  } catch {
    return '';
  }
}

export const MusicActionsMenu: FC<Props> = ({
  tooltipId,
  storyId,
  storyName,
  provider,
  onReseparate,
  reseparateDisabled = false,
  voiceUrlMp3,
  voiceUrlWav,
  bedUrlMp3,
  bedUrlWav,
  originalUrl,
  sizes,
  dubBuffer,
  dubLanguage,
}) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // The open track's MP3 / WAV choice — a cascading submenu positioned to the
  // side of its row. Reset whenever the menu closes.
  const [flyout, setFlyout] = useState<{
    label: string;
    trackType: TrackType;
    formats: FormatOption[];
    x: number;
    y: number;
  } | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelFlyoutClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  // Small grace period so the pointer can travel from the row into the submenu.
  const scheduleFlyoutClose = useCallback(() => {
    cancelFlyoutClose();
    closeTimer.current = setTimeout(() => setFlyout(null), 160);
  }, [cancelFlyoutClose]);
  const close = useCallback(() => {
    cancelFlyoutClose();
    setPos(null);
    setFlyout(null);
  }, [cancelFlyoutClose]);
  useOutsideAlerter(portalRef, close);

  useEffect(() => {
    if (!pos) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pos, close]);

  // This menu is portaled to <body>, OUTSIDE the language dropdown's menu ref.
  // That dropdown (`useAnchoredDropdown`) closes on a native `document`
  // mousedown whose target isn't inside its menu — which would tear this portal
  // down before an item's click fires (downloads silently no-op). Stop mousedown
  // from reaching `document` so the dropdown stays open and clicks land.
  useEffect(() => {
    const node = portalRef.current;
    if (!pos || !node) return undefined;
    const stop = (e: Event) => e.stopPropagation();
    node.addEventListener('mousedown', stop);
    return () => node.removeEventListener('mousedown', stop);
  }, [pos]);

  const entries = useMemo(() => {
    const list: MenuEntry[] = [];
    if (onReseparate)
      list.push({
        kind: 'action',
        label: 'Reprocess Track Separation',
        run: onReseparate,
        disabled: reseparateDisabled,
      });

    // Downloads (PLAT-10715): every track is a row that opens a format flyout —
    // even a single-format track shows its one option. Filenames are
    // `<story-slug>-audio-<descriptor>.<ext>`.
    const slug = slugifyStory(storyName);
    const filename = (descriptor: string, ext: string) =>
      `${slug}-audio-${descriptor}${ext ? `.${ext}` : ''}`;

    // A server-served track: build format options from the available signed URLs
    // (the fetch→blob download names the file client-side).
    const serverTrack = (
      label: string,
      trackType: TrackType,
      descriptor: string,
      variants: { url: string | null; bytes?: number }[],
    ) => {
      const formats: FormatOption[] = [];
      for (const { url, bytes } of variants) {
        if (!url) continue;
        const ext = extFromUrl(url);
        formats.push({
          label: ext ? ext.toUpperCase() : 'Download',
          bytes,
          run: () =>
            void downloadRemote(url, filename(descriptor, ext)).catch(err =>
              logger.error('track download failed', extractErrorInfo(err)),
            ),
        });
      }
      if (formats.length)
        list.push({ kind: 'group', label, trackType, formats });
    };

    serverTrack('Download voice track', 'voice', 'original-voice-split', [
      { url: voiceUrlMp3, bytes: sizes?.voiceMp3 },
      { url: voiceUrlWav, bytes: sizes?.voiceWav },
    ]);
    serverTrack(
      'Download background & music',
      'background_music',
      'original-music-split',
      [
        { url: bedUrlMp3, bytes: sizes?.bedMp3 },
        { url: bedUrlWav, bytes: sizes?.bedWav },
      ],
    );

    if (dubBuffer) {
      const durationSec = dubBuffer.length / dubBuffer.sampleRate;
      list.push({
        kind: 'group',
        label: 'Download AI dub voice',
        trackType: 'ai_dub',
        formats: [
          {
            label: 'MP3',
            // Estimated from the encode bitrate (exact size is only known after
            // encoding); the WAV size below is exact.
            bytes: Math.round(((AI_DUB_KBPS * 1000) / 8) * durationSec),
            run: () =>
              void downloadAudioBufferAsMp3(
                dubBuffer,
                filename('ai-dub-voice', 'mp3'),
              ).catch(err =>
                logger.error(
                  'AI dub MP3 download failed',
                  extractErrorInfo(err),
                ),
              ),
          },
          {
            label: 'WAV',
            bytes: 44 + dubBuffer.length * dubBuffer.numberOfChannels * 2,
            run: () =>
              downloadAudioBuffer(dubBuffer, filename('ai-dub-voice', 'wav')),
          },
        ],
      });
    }

    serverTrack('Download original audio', 'original', 'original-audio', [
      { url: originalUrl, bytes: sizes?.original },
    ]);
    return list;
  }, [
    onReseparate,
    reseparateDisabled,
    storyName,
    voiceUrlMp3,
    voiceUrlWav,
    bedUrlMp3,
    bedUrlWav,
    originalUrl,
    sizes,
    dubBuffer,
  ]);

  if (entries.length === 0) return null;

  const fireDownload = (trackType: TrackType, run: () => void) => {
    trackMusicTrackDownloaded({
      story_id: storyId,
      track_type: trackType,
      provider: provider ?? 'unknown',
      language: dubLanguage ?? '',
    });
    run();
  };

  // Open a track's MP3 / WAV submenu beside its row — to the right, flipping to
  // the left when it would overflow the viewport.
  const openFlyout = (
    entry: Extract<MenuEntry, { kind: 'group' }>,
    rowEl: HTMLElement,
  ) => {
    cancelFlyoutClose();
    const r = rowEl.getBoundingClientRect();
    const subH = MENU_ITEM_HEIGHT * entry.formats.length + 12;
    const x =
      r.right + SUBMENU_WIDTH <= window.innerWidth
        ? r.right - 2
        : r.left - SUBMENU_WIDTH + 2;
    const y = Math.min(r.top, window.innerHeight - subH - 8);
    setFlyout({
      label: entry.label,
      trackType: entry.trackType,
      formats: entry.formats,
      x,
      y,
    });
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (pos) {
      close();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setPos(
      clampMenuToViewport(
        rect.right - MENU_WIDTH,
        rect.bottom + 4,
        MENU_WIDTH,
        MENU_ITEM_HEIGHT * entries.length,
      ),
    );
  };

  const wrap = (cb: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    cb();
  };

  return (
    <>
      <KebabButton
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltipId ? 'Music actions' : undefined}
        onClick={handleClick}
        aria-label="Music and sounds actions"
        data-testid="music-actions-trigger"
      >
        <MoreVertIcon fillColor="currentColor" />
      </KebabButton>
      {pos &&
        createPortal(
          <ContextMenuSurface
            ref={portalRef}
            // The menu lives inside the language dropdown (portaled at
            // `languageDropdownMenu`); the shared surface's resting z-index
            // (100001) is far below it, so it opened *behind* the dropdown.
            // `languageDropdownPopover` sits one step above the dropdown.
            style={{
              top: pos.y,
              left: pos.x,
              zIndex: CLIP_PREVIEW_Z.languageDropdownPopover,
            }}
            data-testid="music-actions-menu"
          >
            {entries.map((entry, i) => {
              // A rule between the action (Reprocess) and the downloads.
              const dividerBefore =
                i > 0 &&
                entries[i - 1].kind === 'action' &&
                entry.kind !== 'action';

              if (entry.kind === 'action') {
                return (
                  <ContextMenuItem
                    key={entry.label}
                    onClick={wrap(entry.run)}
                    disabled={entry.disabled}
                  >
                    {entry.label}
                  </ContextMenuItem>
                );
              }

              // Track group: hovering / clicking the row opens a cascading
              // format submenu beside it (PLAT-10715).
              const isOpen = flyout?.label === entry.label;
              return (
                <Fragment key={entry.label}>
                  {dividerBefore && <MenuDivider role="separator" />}
                  <ContextMenuItem
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    onMouseEnter={event =>
                      openFlyout(entry, event.currentTarget)
                    }
                    onMouseLeave={scheduleFlyoutClose}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      openFlyout(entry, event.currentTarget);
                    }}
                  >
                    <DownloadIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />{' '}
                    {entry.label}
                    <Caret aria-hidden="true">›</Caret>
                  </ContextMenuItem>
                </Fragment>
              );
            })}
            {flyout && (
              <ContextMenuSurface
                onMouseEnter={cancelFlyoutClose}
                onMouseLeave={scheduleFlyoutClose}
                style={{
                  top: flyout.y,
                  left: flyout.x,
                  minWidth: SUBMENU_WIDTH,
                  zIndex: CLIP_PREVIEW_Z.languageDropdownPopover,
                }}
                data-testid="music-actions-submenu"
              >
                {flyout.formats.map(f => (
                  <ContextMenuItem
                    key={f.label}
                    onClick={wrap(() => fireDownload(flyout.trackType, f.run))}
                  >
                    {f.label}
                    {f.bytes != null && <Size>{formatBytes(f.bytes)}</Size>}
                  </ContextMenuItem>
                ))}
              </ContextMenuSurface>
            )}
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

const MenuDivider = styled.div`
  height: 1px;
  margin: 4px 0;
  background: var(--border);
`;

/** Submenu-affordance caret on a track row (points to its format flyout). */
const Caret = styled.span`
  margin-left: auto;
  padding-left: 12px;
  font-size: 16px;
  line-height: 1;
  color: var(--text-muted);
`;

/** File size shown to the right of a format label in the flyout (PLAT-10715). */
const Size = styled.span`
  margin-left: auto;
  padding-left: 16px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
`;
